import React, { useContext, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "../utils/axios";
import { NavContext } from "../contexts/NavContext";
import logger from "../utils/logger";
import { CrosswordSessionDto } from "../models/crossword-session-dto";
import { SignalRProvider } from "../contexts/SignalRContext";
import CrosswordGridClues from "../components/CrosswordGridClues";
import { JitsiMeeting } from "@jitsi/react-sdk";
import { getUser } from "../utils/auth";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import UserStatsModal from "../components/UserStatsModal";
import { UserStatsQueryDto } from "../models/user-stats-dto";
import { MeetingType } from "../models/meeting-type";
import { useSignalR } from "../contexts/useSignalR";
import "./SessionPage.css";

interface MeetingLinkUpdate {
  sessionId: number;
  meetingUrl: string | null;
}

const MeetingLinkUpdates: React.FC<{
  sessionId?: string;
  setSession: React.Dispatch<React.SetStateAction<CrosswordSessionDto | null>>;
}> = ({ sessionId, setSession }) => {
  const { connection } = useSignalR();

  useEffect(() => {
    if (!connection || !sessionId) return;

    const handleMeetingLinkUpdate = (update: MeetingLinkUpdate) => {
      if (update.sessionId === Number(sessionId)) {
        setSession((current) => current ? { ...current, meetingUrl: update.meetingUrl ?? "" } : current);
      }
    };

    connection.on("ReceiveMeetingLinkUpdate", handleMeetingLinkUpdate);
    return () => connection.off("ReceiveMeetingLinkUpdate", handleMeetingLinkUpdate);
  }, [connection, sessionId, setSession]);

  return null;
};

const SessionPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const viewonly = searchParams.get("viewonly") || "false";
  const [session, setSession] = useState<CrosswordSessionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [crosswordId, setCrosswordId] = useState<number | null>(null);
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);
  const [statsData, setStatsData] = useState<UserStatsQueryDto | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [meetingLink, setMeetingLink] = useState("");
  const [savingMeetingLink, setSavingMeetingLink] = useState(false);
  const [meetingLinkError, setMeetingLinkError] = useState<string | null>(null);
  const [editingMeetingLink, setEditingMeetingLink] = useState(false);

  // Initialize showMeeting from localStorage or default to true if not found
  const [showMeeting, setShowMeeting] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(`showMeeting`);
      return stored !== null ? JSON.parse(stored) : true;
    } catch (e) {
      logger.error("Error reading from localStorage:", e);
      return true;
    }
  });

  const user = getUser() || { username: "unknown", email: "unknown", isAdmin: false };
  const domain = import.meta.env.DEV ? "localhost:8443" : "meet.cpsd.org.uk";

  // Get the setSessionName function from NavContext
  const { setTitle: setSessionName } = useContext(NavContext);

  // Update localStorage when showMeeting changes
  useEffect(() => {
    try {
      localStorage.setItem(`showMeeting`, JSON.stringify(showMeeting));
    } catch (e) {
      logger.error("Error writing to localStorage:", e);
    }
  }, [showMeeting, sessionId]);

  useEffect(() => {
    const joinAndFetchSession = async () => {
      try {
        if (!sessionId) {
          setError("No session ID provided");
          setLoading(false);
          return;
        }

        setLoading(true);
        logger.debug(`Joining session ${sessionId} (viewonly=${viewonly})`);
        if (viewonly === "false") {
          // First call API to join the session
          await axios.post(`/api/cross/session/${sessionId}/join`);
        }

        // Then fetch the session data
        const response = await axios.get<CrosswordSessionDto>(`/api/cross/session?id=${sessionId}`);
        setSession(response.data);
        setCrosswordId(response.data.crosswordId);
        setSessionName(response.data.name);
      } catch (err) {
        setError("Failed to load session");
        logger.error(err);
      } finally {
        setLoading(false);
      }
    };

    joinAndFetchSession();
  }, [sessionId, setSessionName, viewonly]);

  // Toggle function that updates state and localStorage
  const toggleShowMeeting = () => {
    setShowMeeting((prev) => !prev);
  };

  // Function to fetch and display stats
  const handleShowStats = async () => {
    if (!sessionId) return;
    
    setLoadingStats(true);
    try {
      const response = await axios.get<UserStatsQueryDto>(`/api/user/stats?sessionId=${sessionId}`);
      setStatsData(response.data);
      setIsStatsModalOpen(true);
    } catch (err) {
      logger.error("Failed to fetch stats:", err);
      // You could add error handling here, maybe a toast notification
    } finally {
      setLoadingStats(false);
    }
  };

  const saveMeetingLink = async () => {
    if (!sessionId) return;

    setSavingMeetingLink(true);
    setMeetingLinkError(null);
    try {
      const response = await axios.put<{ meetingUrl: string }>(`/api/cross/session/${sessionId}/meeting-link`, {
        meetingUrl: meetingLink,
      });
      setSession((current) => current ? { ...current, meetingUrl: response.data.meetingUrl } : current);
      setEditingMeetingLink(false);
    } catch (err) {
      logger.error("Failed to save Teams meeting link:", err);
      setMeetingLinkError("Unable to save that Teams meeting link.");
    } finally {
      setSavingMeetingLink(false);
    }
  };

  const removeMeetingLink = async () => {
    if (!sessionId) return;

    setSavingMeetingLink(true);
    setMeetingLinkError(null);
    try {
      await axios.delete(`/api/cross/session/${sessionId}/meeting-link`);
      setSession((current) => current ? { ...current, meetingUrl: "" } : current);
      setMeetingLink("");
      setEditingMeetingLink(false);
    } catch (err) {
      logger.error("Failed to remove Teams meeting link:", err);
      setMeetingLinkError("Unable to remove that Teams meeting link.");
    } finally {
      setSavingMeetingLink(false);
    }
  };

  if (loading) return <div>Loading session...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!session) return <div>Session not found</div>;

  return (
    <SignalRProvider>
      <MeetingLinkUpdates sessionId={sessionId} setSession={setSession} />
      <div className="session-page">
        <div className="content-container">
          {session.meetingType === MeetingType.Jitsi && (
            <div className={`resizable-container ${showMeeting ? "" : "collapsed"}`}>
              <div className="container-controls">
                <button className="slider-toggle" onClick={toggleShowMeeting} aria-label="Toggle meeting visibility">
                  {showMeeting ? <EyeSlashIcon className="h-6 w-6" /> : <EyeIcon className="h-6 w-6" />}
                </button>
              </div>
              {showMeeting && (
                <JitsiMeeting
                  roomName={"Crossword" + sessionId}
                  domain={domain}
                  userInfo={{ displayName: user.username, email: user.email }}
                  getIFrameRef={(iframeRef) => {
                    iframeRef.style.height = "100%";
                    iframeRef.style.width = "100%";
                    const innerIframe = iframeRef.querySelector("iframe");
                    if (innerIframe) {
                      innerIframe.style.height = "100%";
                      innerIframe.style.width = "100%";
                      innerIframe.style.display = "block";
                    }
                  }}
                />
              )}
            </div>
          )}
          {session.meetingType === MeetingType.Teams && (
            <section className="teams-meeting">
              <div className="teams-meeting-actions">
                <span>Teams</span>
                <button
                  onClick={() => {
                    setMeetingLink(session.meetingUrl ?? "");
                    setMeetingLinkError(null);
                    setEditingMeetingLink(true);
                  }}
                >
                  {session.meetingUrl ? "Change" : "Add"}
                </button>
                {session.meetingUrl && (
                  <a href={session.meetingUrl} target="_blank" rel="noreferrer">Join</a>
                )}
              </div>
              {editingMeetingLink && (
                <>
                  <label>
                    Teams meeting link
                    <input
                      type="url"
                      value={meetingLink}
                      onChange={(event) => setMeetingLink(event.target.value)}
                      placeholder="https://teams.microsoft.com/..."
                    />
                  </label>
                  <button onClick={saveMeetingLink} disabled={savingMeetingLink || !meetingLink.trim()}>
                    {savingMeetingLink ? "Saving..." : "Save Teams meeting link"}
                  </button>
                  {session.meetingUrl && (
                    <button onClick={removeMeetingLink} disabled={savingMeetingLink}>
                      Remove Teams meeting
                    </button>
                  )}
                  {meetingLinkError && <p role="alert">{meetingLinkError}</p>}
                </>
              )}
            </section>
          )}
          {crosswordId && crosswordId > 0 ? (
            <CrosswordGridClues
              key={`crossword-${crosswordId}-${sessionId}`}
              crosswordId={crosswordId}
              readonly={viewonly === "true"}
              sessionId={Number(sessionId ?? "0")}
              mobile={false}
              onShowStats={handleShowStats}
              loadingStats={loadingStats}
            />
          ) : (
            <div>Error: Session has no valid crossword ID</div>
          )}
        </div>
        
        <UserStatsModal 
          isOpen={isStatsModalOpen}
          onClose={() => setIsStatsModalOpen(false)}
          statsData={statsData}
        />
      </div>
    </SignalRProvider>
  );
};

export default SessionPage;
